import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import GlassModal from './Common/GlassModal';
import { hasTimeConflict, getEmployeeSchedule } from '../utils/smartScheduling';
import { fmtTime } from '../utils/dateFormatters';

/**
 * ExtendJobModal
 *
 * Purpose: Allow users to extend job duration when it runs over scheduled time
 * Shows quick options (30min, 1hr, 2hr, 4hr) plus custom input
 * Updates scheduled_end time and keeps job in 'in_progress' status
 *
 * Part of: Phase 2 - Smart Status Progression
 */

const ExtendJobModal = ({
  isOpen,
  onClose,
  onConfirm,
  jobTitle,
  currentScheduledEnd,
  // Props for conflict checking
  jobId,
  assignedTo,
  companyId
}) => {
  const [extensionMinutes, setExtensionMinutes] = useState(60); // Default 1 hour
  const [customMinutes, setCustomMinutes] = useState('');
  const [reason, setReason] = useState('');
  const [conflictWarning, setConflictWarning] = useState(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);

  // Check for scheduling conflicts when extension changes
  useEffect(() => {
    const checkForConflict = async () => {
      if (!currentScheduledEnd || !extensionMinutes || !assignedTo || !companyId || !jobId) {
        setConflictWarning(null);
        return;
      }

      setIsCheckingConflict(true);

      try {
        const currentEnd = new Date(currentScheduledEnd);
        const newEnd = new Date(currentEnd.getTime() + extensionMinutes * 60000);

        // Get schedule for the employee after current end time
        const searchStart = new Date(currentEnd.getTime() + 15 * 60000); // 15 min after current end
        const searchEnd = new Date(newEnd.getTime() + 4 * 60 * 60 * 1000); // 4 hours after new end

        const existingEvents = await getEmployeeSchedule(assignedTo, companyId, searchStart, searchEnd);

        // Check if new end time conflicts with any existing event
        const hasConflict = hasTimeConflict(existingEvents, currentEnd, newEnd, { default_buffer_before_minutes: 0, default_buffer_after_minutes: 30 });

        if (hasConflict) {
          // Find the conflicting event
          const conflictingEvent = existingEvents.find(event => {
            const es = new Date(event.start_time).getTime();
            const ee = new Date(event.end_time).getTime();
            const bufferedEnd = ee + 30 * 60 * 1000;
            return currentEnd.getTime() < bufferedEnd && newEnd.getTime() > es - 30 * 60 * 1000;
          });

          if (conflictingEvent) {
            const conflictTime = fmtTime(conflictingEvent.start_time, {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
            setConflictWarning({
              title: conflictingEvent.title || 'Next Job',
              time: conflictTime
            });
          } else {
            setConflictWarning({ title: 'Next Job', time: 'Unknown' });
          }
        } else {
          setConflictWarning(null);
        }
      } catch (error) {
        console.error('Error checking conflict:', error);
        setConflictWarning(null);
      } finally {
        setIsCheckingConflict(false);
      }
    };

    checkForConflict();
  }, [currentScheduledEnd, extensionMinutes, assignedTo, companyId, jobId]);

  // Early return AFTER all hooks
  if (!isOpen) return null;

  const quickOptions = [
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '2 hours', value: 120 },
    { label: '4 hours', value: 240 },
  ];

  const handleQuickSelect = (minutes) => {
    setExtensionMinutes(minutes);
    setCustomMinutes('');
  };

  const handleCustomChange = (e) => {
    const value = e.target.value;
    setCustomMinutes(value);
    if (value && !isNaN(value) && parseInt(value) > 0) {
      setExtensionMinutes(parseInt(value));
    }
  };

  const handleConfirm = () => {
    if (extensionMinutes <= 0) return;

    // If there's a conflict warning, require explicit confirmation
    if (conflictWarning) {
      const proceed = window.confirm(
        `Extending this job would overlap with "${conflictWarning.title}" at ${conflictWarning.time}. Continue anyway?`
      );
      if (!proceed) return;
    }

    onConfirm({
      extensionMinutes,
      reason: reason.trim() || 'Job extended'
    });
    onClose();
  };

  const calculateNewEndTime = () => {
    if (!currentScheduledEnd || !extensionMinutes) return null;

    const endDate = new Date(currentScheduledEnd);
    endDate.setMinutes(endDate.getMinutes() + extensionMinutes);

    return endDate.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      closeOnOverlay={false}
      header={(
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-white">Extend Job</h2>
          </div>
          <button type="button" aria-label="Close" title="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </>
      )}
    >

        {/* Body */}
        <div>
          <p className="text-gray-400 mb-6">
            <span className="font-medium text-white">{jobTitle}</span> needs more time? Add additional hours to continue working.
          </p>

          {/* Quick Options */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Quick Select
            </label>
            <div className="grid grid-cols-4 gap-2">
              {quickOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleQuickSelect(option.value)}
                  className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                    extensionMinutes === option.value && !customMinutes
                      ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                      : 'border-white/20 hover:border-white/40 text-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Duration */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Custom Duration (minutes)
            </label>
            <input
              type="number"
              min="1"
              max="480"
              value={customMinutes}
              onChange={handleCustomChange}
              placeholder="Enter custom minutes"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum 8 hours (480 minutes)
            </p>
          </div>

          {/* Reason (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why does this job need more time?"
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* New End Time Preview */}
          {calculateNewEndTime() && (
            <div className="space-y-3">
              {/* Conflict Warning */}
              {conflictWarning && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-300 mb-1">
                        Scheduling Conflict Detected
                      </p>
                      <p className="text-sm text-red-400">
                        Extending this job would overlap with <strong>{conflictWarning.title}</strong> at {conflictWarning.time}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-500/10 backdrop-blur-sm border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <ExclamationCircleIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-300 mb-1">
                      New Scheduled End Time
                    </p>
                    <p className="text-sm text-blue-200">
                      {calculateNewEndTime()}
                    </p>
                    <p className="text-xs text-blue-400 mt-1">
                      Adding {extensionMinutes} minutes to current schedule
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-white/5 backdrop-blur-sm">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!extensionMinutes || extensionMinutes <= 0}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Extend Job
          </button>
        </div>
    </GlassModal>
  );
};

export default ExtendJobModal;

